<template>
  <div class="home-page-wrapper">
    <!-- Floating Cannabis Leaves -->
    <div 
      class="floating-leaf floating-leaf-left"
      :class="{ 'visible': showLeaves }"
      :style="{ opacity: leafOpacity }"
    >
      <img src="@/assets/images/cannabis-leaf-left.webp" alt="" />
    </div>
    <div 
      class="floating-leaf floating-leaf-right"
      :class="{ 'visible': showLeaves }"
      :style="{ opacity: leafOpacity }"
    >
      <img src="@/assets/images/cannabis-leaf-right.webp" alt="" />
    </div>
    
    <section id="hero-background" class="hero">
      <div class="hero-backdrop" aria-hidden="true">
        <img
          :src="bannerFallback"
          alt=""
          class="hero-bg"
          fetchpriority="high"
          decoding="async"
        />
        <div class="hero-gradient-overlay"></div>
      </div>
      <div class="container-fluid p-0 px-0 hero-carousel-container">
        <HeroCarousel />
      </div>
    </section>
      <!-- Latest Products Section - Shows before CTA when no sale products -->
  <section class="products-section py-5" v-if="latestProducts.length > 0">
    <div class="container px-md-3 px-0">
      <div class="d-flex flex-column align-items-center px-3">
        <h2 class="display-4 text-center mb-2 text-white text-uppercase font-good-brush">Últimos agregados</h2>
        <p class="text-center text-white-50">Descubre los productos más recientes en nuestro <a href="/productos" class="text-white-50 fw-light">catálogo</a>.</p>
      </div>
      <div class="carousel-container">
        <ProductsCarousel 
          :products="latestProducts"
          :is-loading="loadingLatest"
        />
      </div>
    </div>
  </section>

    <!-- Distributor Information Section -->
  <section class="distributor-section py-5 bg-black">
    <div class="container">
      <div class="row align-items-center d-flex justify-content-center align-items-center">
        <div class="col-lg-5 d-flex justify-content-lg-end justify-content-center align-items-center mb-4 mb-lg-0">
          <div class="distributor-image-container d-flex justify-content-lg-end justify-content-center align-items-center">
            <img src="@/assets/images/jamrock-logo-iguana.webp" alt="Distribuidora de productos para cultivo" class="col-8 img-fluid">
          </div>
        </div>
        <div class="col-lg-7">
          <div class="distributor-content">
            <h2 class="display-4 mb-3 text-white text-uppercase font-good-brush">Tu Socio en tu Cultivo</h2>
            <p class="mb-4 lead text-white-50">Iniciamos en el 2011 con el fin de promover el uso conciente y el autocultivo del cannabis, tenemos mas de 10 años de experiencia en el rubro ofreciendote productos de primera calidad y asesoramiento personalizado durante todo tu cultivo.</p>  
            <a href="/productos" class="btn btn-primary shadow-sm">
              <FaIcon name="leaf" class="me-2" />Explorá nuestro catálogo
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>

  
<!-- Products Section - On Sale (Only shown if products exist) -->
  <section class="products-section py-5" v-if="onSaleProducts.length > 0">
    <div class="container px-md-3 px-0">
      <h2 class="display-4 text-center mb-4 text-white text-uppercase px-3 font-good-brush">Productos En Oferta</h2>
      <div class="carousel-container">
        <ProductsCarousel 
          :products="onSaleProducts"
          :is-loading="loadingOnSale"
        />
      </div>
    </div>
  </section>

  <section class="products-section pb-5 pt-0">
    <div class="container px-md-3 px-0">
      <ReviewsCarousel />
    </div>
  </section>
  
  <!-- Blog Section -->
  <section class="blog-section py-5">
    <div class="container px-3">
      <!-- Header -->
      <div class="blog-section-header text-center mb-5">
        <h2 class="display-5 text-white text-uppercase mb-3 fw-bold font-good-brush">Últimos Artículos</h2>
        <p class="lead text-white-50 mx-auto" style="max-width: 600px;">
          Descubre consejos, guías y novedades sobre cultivo y cuidado de plantas
        </p>
      </div>
      
      <!-- Loading State -->
      <div v-if="loadingBlogs" class="row g-4">
        <div v-for="n in 3" :key="n" class="col-lg-4 col-md-6">
          <div class="blog-skeleton">
            <div class="skeleton-image"></div>
            <div class="skeleton-body">
              <div class="skeleton-badge"></div>
              <div class="skeleton-title"></div>
              <div class="skeleton-text"></div>
              <div class="skeleton-text short"></div>
              <div class="skeleton-meta"></div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Blog Posts Grid -->
      <div v-else-if="latestBlogs.length > 0" class="row g-4" :class="{ 'justify-content-center': latestBlogs.length === 1 }">
        <div 
          v-for="(blog, index) in latestBlogs" 
          :key="blog.id" 
          class="col-lg-4 col-md-6"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <BlogCard :blog="blog" class="blog-card-animated" />
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-else class="text-center py-5">
        <FaIcon name="pen-fancy" class="fa-3x text-white-50 mb-3" />
        <p class="text-white-50">Próximamente nuevos artículos</p>
      </div>
      
      <!-- CTA Button -->
      <div v-if="latestBlogs.length > 0" class="text-center mt-5">
        <router-link to="/blog" class="btn btn-blog-cta btn-lg">
          <span>Ver todos los artículos</span>
          <FaIcon name="arrow-right" class="ms-2" />
        </router-link>
      </div>
    </div>
  </section>
  
  <FaqSection class="faq-section" />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import bannerFallback from '@/assets/images/banner-fallback.png';
import HeroCarousel from '@/components/HeroCarousel.vue';
import ProductsCarousel from '@/components/ProductsCarousel.vue';
import FaqSection from '@/components/FaqSection.vue';
import ReviewsCarousel from '@/components/ReviewsCarousel.vue';
import BlogCard from '@/components/BlogCard.vue';
import { getBlogPosts } from '@/services/blog';

// Vuex store
const store = useStore();

// Floating leaves state
const showLeaves = ref(false);
const leafOpacity = ref(0);

// Handle scroll for floating leaves.
// Pattern: all DOM reads happen before any Vue reactive writes to avoid
// forced synchronous layouts (read → write → read causes reflow).
let _scrollRafPending = false;
const handleScroll = () => {
  if (_scrollRafPending) return;
  _scrollRafPending = true;
  requestAnimationFrame(() => {
    _scrollRafPending = false;

    // ── READS (batch all layout queries before any writes) ──────────────
    const scrollY = window.scrollY;
    const footer = document.querySelector('footer') || document.querySelector('.faq-section');
    const footerRect = footer ? footer.getBoundingClientRect() : null;
    const windowHeight = window.innerHeight;

    // ── WRITES (Vue reactive assignments trigger DOM updates) ────────────
    if (scrollY > 400) {
      showLeaves.value = true;
      if (footerRect) {
        const fadeStartDistance = 300;
        const distanceToFooter = footerRect.top - windowHeight;
        if (distanceToFooter < fadeStartDistance && distanceToFooter > -footerRect.height) {
          leafOpacity.value = Math.max(0, distanceToFooter / fadeStartDistance);
        } else if (distanceToFooter >= fadeStartDistance) {
          leafOpacity.value = 1;
        } else {
          leafOpacity.value = 0;
        }
      } else {
        leafOpacity.value = 1;
      }
    } else {
      showLeaves.value = false;
      leafOpacity.value = 0;
    }
  });
};

// Computed properties from Vuex store
const onSaleProducts = computed(() => {
  const products = store.getters['products/onSaleProducts'];
  console.log('On sale products computed:', products);
  return products;
});
const latestProducts = computed(() => {
  const products = store.getters['products/regularProducts'];
  console.log('Latest products computed:', products);
  return products;
});
const loadingOnSale = computed(() => {
  const loading = store.state.products.loadingOnSale;
  console.log('Loading on sale:', loading);
  return loading;
});
const loadingLatest = computed(() => {
  const loading = store.state.products.loading;
  console.log('Loading latest:', loading);
  return loading;
});

const latestBlogs = ref([]);
const loadingBlogs = ref(true);

onMounted(async () => {
  // Add scroll listener for floating leaves
  window.addEventListener('scroll', handleScroll);
  handleScroll(); // Check initial position
  
  try {
    console.log('Home component mounted, loading data...');
    
    // 1. Precargar productos en oferta de inmediato (sin bloquear UI)
    store.dispatch('products/preloadProductsOnSale');
    
    // 2. Cargar productos en oferta prioritariamente
    const onSalePromise = store.dispatch('products/fetchProductsOnSale').catch(error => {
      console.error('Error loading products on sale:', error);
    });
    
    // 3. Esperar solo productos en oferta
    await onSalePromise;
    console.log('On sale products loaded');
    
    // 4. Cargar productos normales después (lazy loading)
    setTimeout(() => {
      store.dispatch('products/fetchProducts').catch(error => {
        console.error('Error loading latest products:', error);
      });
    }, 500); // Retraso de 500ms para priorizar productos en oferta
    
    console.log('Home initialization completed');
    
    // Cargar últimos blogs
    loadingBlogs.value = true;
    try {
      const data = await getBlogPosts({ status: 'published', page_size: 3 });
      latestBlogs.value = data.results || [];
    } catch (error) {
      console.error('Error loading blog posts:', error);
      latestBlogs.value = [];
    } finally {
      loadingBlogs.value = false;
    }
    
  } catch (error) {
    console.error('Error initializing home:', error);
  }
  
  // Initialize ScrollReveal after DOM is fully loaded, but only for desktop
  nextTick(() => {
    // Check if we're on mobile
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      // For mobile, make all elements visible immediately without animations
      const elementsToShow = document.querySelectorAll(
        '.distributor-section .distributor-image-container, .distributor-section .distributor-content, ' +
        '.products-section h2, .products-section .carousel-container, .category-card, ' +
        '.blog-section h2, .blog-section p.lead, .blog-card'
      );
      
      elementsToShow.forEach(el => {
        if (el) {
          el.style.opacity = '1';
          el.style.transform = 'none';
          el.style.visibility = 'visible';
        }
      });      console.log('Mobile detected: ScrollReveal disabled');
    } else {
      // For desktop, initialize ScrollReveal with a delay
      setTimeout(() => {
        initializeScrollReveal().catch(() => {});
      }, 100);
    }
  });
});

// Simplified ScrollReveal initialization (carga diferida del paquete ~19KB)
const initializeScrollReveal = async () => {
  const isMobile = window.innerWidth < 768;
  
  // Don't use ScrollReveal on mobile devices
  if (isMobile) {
    console.log('ScrollReveal disabled on mobile devices');
    // Make all elements visible immediately without animations
    const elementsToShow = document.querySelectorAll(
      '.distributor-section .distributor-image-container, .distributor-section .distributor-content, ' +
      '.products-section h2, .products-section .carousel-container, .category-card, ' +
      '.blog-section h2, .blog-section p.lead, .blog-card'
    );
    
    elementsToShow.forEach(el => {
      if (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.visibility = 'visible';
      }
    });
    
    return;
  }
  
  // Only for desktop devices
  const { default: ScrollReveal } = await import('scrollreveal');
  const sr = ScrollReveal({
    distance: '60px',
    duration: 1000,
    delay: 300,
    origin: 'bottom',
    reset: false,
    mobile: false,
    viewFactor: 0.1
  });

  // Apply to distributor section elements with mobile-friendly settings
  sr.reveal('.distributor-section .distributor-image-container', {
    origin: isMobile ? 'bottom' : 'left',
    delay: isMobile ? 200 : 400,
    distance: isMobile ? '20px' : '60px'
  });
  
  sr.reveal('.distributor-section .distributor-content', {
    origin: isMobile ? 'bottom' : 'right',
    delay: isMobile ? 250 : 400,
    distance: isMobile ? '20px' : '60px'
  });

  // Apply to product sections with mobile-friendly settings
  sr.reveal('.products-section h2', {
    origin: 'top',
    delay: isMobile ? 150 : 300,
    distance: isMobile ? '20px' : '30px'
  });
  
  sr.reveal('.products-section .carousel-container', {
    origin: 'bottom',
    delay: isMobile ? 200 : 400,
    distance: isMobile ? '20px' : '40px'
  });

  // Apply to category cards - staggered effect with mobile optimization
  document.querySelectorAll('.category-card').forEach((card, index) => {
    sr.reveal(card, {
      delay: isMobile ? 150 + (index * 50) : 300 + (index * 100),
      distance: isMobile ? '15px' : '30px',
      origin: 'bottom'
    });
  });
  
  // Blog section animations with mobile optimization
  sr.reveal('.blog-section h2', {
    origin: 'top',
    delay: isMobile ? 150 : 300,
    distance: isMobile ? '15px' : '30px'
  });
  
  sr.reveal('.blog-section p.lead', {
    origin: 'top',
    delay: isMobile ? 200 : 400,
    distance: isMobile ? '15px' : '30px'
  });
  
  // Apply to blog cards - staggered effect with mobile optimization
  document.querySelectorAll('.blog-card').forEach((card, index) => {
    sr.reveal(card, {
      delay: isMobile ? 150 + (index * 75) : 300 + (index * 150),
      distance: isMobile ? '20px' : '40px',
      origin: 'bottom'
    });
  });
    // Handle window resize - disable/enable based on viewport width
  window.addEventListener('resize', () => {
    const newIsMobile = window.innerWidth < 768;
    if (newIsMobile && !isMobile) {
      // Changed to mobile, disable ScrollReveal
      sr.destroy();
      
      // Make all elements visible immediately
      const elementsToShow = document.querySelectorAll(
        '.distributor-section .distributor-image-container, .distributor-section .distributor-content, ' +
        '.products-section h2, .products-section .carousel-container, .category-card, ' +
        '.blog-section h2, .blog-section p.lead, .blog-card'
      );
      
      elementsToShow.forEach(el => {
        if (el) {
          el.style.opacity = '1';
          el.style.transform = 'none';
          el.style.visibility = 'visible';
        }
      });
    } else if (!newIsMobile && isMobile) {
      // Changed to desktop, reload to re-enable ScrollReveal
      location.reload();
    }
  });
}

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<script>
export default {
  name: "AppHome"
};
</script>

<style scoped>
/* Floating Cannabis Leaves */
.floating-leaf {
  position: fixed;
  bottom: 0;
  z-index: 100;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.floating-leaf img {
  width: 200px;
  height: auto;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  opacity: .5;
}

.floating-leaf-left {
  left: 0;
  transform: translateX(-100%);
}

.floating-leaf-right {
  right: 0;
  transform: translateX(100%);
}

.floating-leaf.visible {
  opacity: 1;
}

.floating-leaf-left.visible {
  transform: translateX(0);
}

.floating-leaf-right.visible {
  transform: translateX(0);
}

/* Hide leaves on mobile and tablets */
@media (max-width: 1200px) {
  .floating-leaf {
    display: none;
  }
}

/* Home page wrapper with background texture */
.home-page-wrapper {
  position: relative;
  width: 100%;
  min-height: 100vh;
}

.hero-carousel-container {
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
  z-index: 1;
}

.hero {
  position: relative;
  width: 100%;
  min-height: 90vh;
  background-color: #000;
  overflow: hidden;
  margin: 0;
  padding: 0;
  z-index: 1;
}

.hero-backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.hero-gradient-overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.45) 45%, rgba(0, 0, 0, 0.82) 78%, #000 100%),
    linear-gradient(to right, rgba(0, 0, 0, 0.35) 0%, transparent 35%, transparent 65%, rgba(0, 0, 0, 0.35) 100%);
  pointer-events: none;
}

.hero-title {
  font-size: 4rem;
  font-weight: 700;
  line-height: 1.1;
}

@keyframes waveEffect {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%; 
  }
  100% {
    background-position: 0% 50%;
  }
}

.hero-wrapper {
  width: 100%;
  position: relative;
  z-index: 1;
  padding: 2rem 0;
}

.stock-badge {
  background-color: #222;
  padding: 1rem;
  border-radius: 0.5rem;
  color: white;
  transform: rotate(-3deg);
  width: fit-content;
  margin-top: -30px;
}
.stock-badge h2{
  font-size: 1.3rem;
}

.product-image {
  position: relative;
  transition: transform 0.3s ease;
  max-height: 400px;
  height: 100%;
  overflow: hidden;
  border-radius: 1rem;
  animation: fadeIn 1.5s ease-out;
  transition: 1s ease;
  filter: brightness(.8);
}

.product-image:hover {
  transform: scale(1.02);
  filter: brightness(1);
}

.combo-image {
  animation: appearFromMiddle .3s ease-out;
  filter: drop-shadow(0 0 .5rem rgba(58, 58, 58, 0.3));
}

.hero-text {
  animation: appearFromMiddle .5s ease-out;
}

@keyframes appearFromMiddle {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}


/* Responsive adjustments */
@media (max-width: 991.98px) {
  .hero {
    min-height: var(--hero-height, 100vh);
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .product-image {
    margin-top: 2rem;
  }
}

@media (max-width: 768px) {
  .hero {
    margin-bottom: 0;
  }
  
  .section-title {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
  .product-image {
    margin-top: 2rem;
  }
  
  .products-section {
    padding-top: 2rem !important;
    margin-top: 0 !important;
  }
}

@media (max-width: 480px) {
  .hero {
    margin-bottom: 0;
  }
  
  .products-section {
    padding-top: 1.5rem !important;
    margin-top: 0 !important;
  }
}

.display-2 {
  letter-spacing: 2px;
  line-height: 1.1;
}

.carousel-indicators {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -100px;
  padding: 0;
  z-index: 15;
}

.carousel-indicators button {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  border: 2px solid transparent;
  opacity: 0.5;
  transition: all 0.3s ease;
}

.carousel-indicators button.active {
  background-color: var(--white-color);
  opacity: 1;
  transform: scale(1.2);
}

/* Remove default Bootstrap carousel controls if you don't need them */
.carousel-control-prev,
.carousel-control-next {
  display: none;
}

.products-section {
  min-height: auto;
  background-color: var(--black-color);
  position: relative;
  z-index: 1;
  margin-top: 0; /* Remove top margin to prevent gap */
}

.section-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: var(--white-color) !important;
  text-align: center;
}

@media (max-width: 768px) {
  .hero-carousel-container{
    height: fit-content;
  }
  .hero-title {
    font-size: 3rem;
  }
  .section-title {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
}
.asesoramiento{
  background-color: #ebebeb;
  border-radius: 30px;
  padding: 2rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
}

.asesoramiento img{
  width: auto;
  height: 800px;
}

.asesoramiento-img {
  max-width: 100%;
  height: auto;
  max-height: 800px;
  object-fit: contain;
}

@media (max-width: 991.98px) {
  .asesoramiento-img {
    max-height: 250px;
    margin: 1rem 0;
  }
}

/* Distributor Section Styles */
.distributor-section {
  background-color: #f8f9fa;
  position: relative;
  overflow: hidden;
}

.distributor-image-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.5rem;
}

.distributor-image {
  transition: transform 0.5s ease;
  width: 100%;
  object-fit: cover;
  height: 500px;
}

.distributor-image:hover {
  transform: scale(1.02);
}

.distributor-content {
  padding: 1rem;
}

.text-gradient-primary {
  background: linear-gradient(to right, #2a7d2a, #4caf50);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@media (max-width: 991.98px) {
  .distributor-image {
    height: 350px;
  }
}

@media (max-width: 767.98px) {
  .distributor-section {
    text-align: center;
  }
  
  .distributor-image {
    height: 300px;
  }
}

/* Blog Section Styles */
.blog-section {
  background: linear-gradient(180deg, var(--black-color) 0%, #0a0a0a 50%, var(--black-color) 100%);
  position: relative;
  overflow: hidden;
}

.blog-section-header {
  position: relative;
  z-index: 1;
}

.section-badge {
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  color: var(--primary-color);
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
}

/* Blog Card Animations */
.blog-card-animated {
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Skeleton Loading Styles */
.blog-skeleton {
  background: linear-gradient(145deg, rgba(255,255,255,0.03), rgba(255,255,255,0.06));
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.05);
}

.skeleton-image {
  height: 200px;
  background: linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-body {
  padding: 1.25rem;
}

.skeleton-badge {
  height: 24px;
  width: 80px;
  background: rgba(255,255,255,0.05);
  border-radius: 4px;
  margin-bottom: 0.75rem;
  animation: shimmer 1.5s infinite;
  background-size: 200% 100%;
  background-image: linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%);
}

.skeleton-title {
  height: 28px;
  width: 90%;
  background: rgba(255,255,255,0.05);
  border-radius: 4px;
  margin-bottom: 1rem;
  animation: shimmer 1.5s infinite;
  background-size: 200% 100%;
  background-image: linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%);
}

.skeleton-text {
  height: 16px;
  width: 100%;
  background: rgba(255,255,255,0.05);
  border-radius: 4px;
  margin-bottom: 0.5rem;
  animation: shimmer 1.5s infinite;
  background-size: 200% 100%;
  background-image: linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%);
}

.skeleton-text.short {
  width: 60%;
}

.skeleton-meta {
  height: 14px;
  width: 50%;
  background: rgba(255,255,255,0.05);
  border-radius: 4px;
  margin-top: 1rem;
  animation: shimmer 1.5s infinite;
  background-size: 200% 100%;
  background-image: linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 75%);
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Blog CTA Button */
.btn-blog-cta {
  background: var(--white-color);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: var(--black-color);
  padding: 0.875rem 2rem;
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-blog-cta:hover {
  background: white;
  border-color: white;
  color: var(--black-color);
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(255, 255, 255, 0.15);
}

.btn-blog-cta i {
  transition: transform 0.3s ease;
}

.btn-blog-cta:hover i {
  transform: translateX(4px);
}

/* Responsive Blog Styles */
@media (max-width: 991.98px) {
  .blog-section {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  
  .skeleton-image {
    height: 180px;
  }
}

@media (max-width: 767.98px) {
  .section-badge {
    font-size: 0.75rem;
    padding: 0.4rem 1rem;
  }
  
  .blog-section-header h2 {
    font-size: 1.75rem !important;
  }
  
  .blog-section-header .lead {
    font-size: 1rem;
  }
  
  .btn-blog-cta {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }
  
  .skeleton-image {
    height: 160px;
  }
}

/* Divisor inclinado entre secciones */
.slant-divider {
  position: relative;
  height: 50px;
  overflow: hidden;
  margin-top: -20px;
  margin-bottom: 40px;
}

.slant-divider::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: inherit;
  transform: skewY(-3deg);
  transform-origin: top left;
  z-index: 1;
}

.slant-divider::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background: inherit;
  transform: skewY(3deg);
  transform-origin: top right;
  z-index: 1;
}

</style>